import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice } from "../../design/components";
import { useAuth } from "../../auth/AuthProvider";
import { tabCounts, useInboundDocuments, useInboundStageCounts } from "../../api/inbound";
import { useHeldDocuments, type HeldRow } from "../../api/routing";
import { InboundTabs, TAB_STATE, type InboundTab } from "./InboundTabs";
import { InFlightTable } from "./InFlightTable";
import { HeldPanel, RejectedPanel } from "./InboundIssuesTable";
import { LandedTable } from "./LandedTable";
import { AssignCompanyDialog } from "./AssignCompanyDialog";
import styles from "./inbound.module.css";

const TABS: InboundTab[] = ["in-flight", "held", "landed", "rejected"];

function readTab(raw: string | null, showHeld: boolean): InboundTab {
  const tab = TABS.find((key) => key === raw) ?? "in-flight";
  return tab === "held" && !showHeld ? "in-flight" : tab;
}

export function InboundPage() {
  const { t } = useTranslation();
  const { me, status } = useAuth();
  // Until the session resolves the role is unknown, so a ?tab=held link is left alone.
  const ready = status !== "loading";
  const showHeld = me?.role === "group_admin";
  const [searchParams, setSearchParams] = useSearchParams();
  const [dialogFor, setDialogFor] = useState<HeldRow | null>(null);
  const [assigned, setAssigned] = useState<string | null>(null);

  const tab = readTab(searchParams.get("tab"), showHeld || !ready);

  // A bare /inbound is rewritten so the selected tab is always linkable.
  useEffect(() => {
    if (!ready || searchParams.get("tab") === tab) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", tab);
    setSearchParams(next, { replace: true });
  }, [ready, searchParams, setSearchParams, tab]);

  const select = useCallback(
    (next: InboundTab) => {
      const params = new URLSearchParams(searchParams);
      params.set("tab", next);
      setAssigned(null);
      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const counts = useInboundStageCounts();
  const documents = useInboundDocuments(TAB_STATE[tab]);
  const held = useHeldDocuments(tab === "held" && showHeld);

  const rows = useMemo(
    () => (documents.data?.pages ?? []).flatMap((page) => page.items),
    [documents.data],
  );
  const heldRows = useMemo(
    () => (held.data?.pages ?? []).flatMap((page) => page.items),
    [held.data],
  );

  // A document stopped after routing (unresolved master data, say) has no hold row, so it
  // is listed beside the unrouted holds instead of being counted and hidden (BUG-010).
  const exceptions = useMemo(() => {
    const holding = new Set(heldRows.map((row) => row.documentId));
    return rows.filter((row) => !holding.has(row.id));
  }, [rows, heldRows]);

  const isError = documents.isError || (tab === "held" && held.isError);
  const isLoading = documents.isLoading || (tab === "held" && held.isLoading);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("inbound.page.title", "Inbound Documents")}</h1>
          <p className={styles.lede}>
            {t(
              "inbound.page.lede",
              "Documents suppliers have sent to a company in the group, from arrival to the draft the receiving team reviews.",
            )}
          </p>
        </div>
      </div>

      {assigned ? (
        <div className={styles.pageNotice}>
          <Notice kind="ok">{t("inbound.page.assignedTo", "Assigned to {{name}}.", { name: assigned })}</Notice>
        </div>
      ) : null}

      <section className="card card-tight">
        <div className="card-head wrap">
          <InboundTabs tab={tab} counts={tabCounts(counts.data)} showHeld={showHeld} onSelect={select} />
        </div>

        <div id="inbound-panel" role="tabpanel" aria-labelledby={`inbound-tab-${tab}`}>
          {!ready ? null : isError ? (
            <div className="card-body">
              <Notice kind="fail">The received documents could not be loaded.</Notice>
            </div>
          ) : tab === "in-flight" ? (
            <InFlightTable rows={rows} isLoading={isLoading} />
          ) : tab === "landed" ? (
            <LandedTable rows={rows} isLoading={isLoading} />
          ) : tab === "held" ? (
            <HeldPanel holds={heldRows} exceptions={exceptions} isLoading={isLoading} onAssign={setDialogFor} />
          ) : (
            <RejectedPanel rows={rows} isLoading={isLoading} />
          )}
        </div>
      </section>

      {dialogFor ? (
        <AssignCompanyDialog
          hold={dialogFor}
          onClose={() => setDialogFor(null)}
          onAssigned={(name) => {
            setDialogFor(null);
            setAssigned(name);
          }}
        />
      ) : null}
    </>
  );
}
